// @ts-check
// Configurações (doc 07, 7.6; doc 11, seções 2.1 e 2.2): seção comum, Perfil (foto, nome, e-mail) e Experiência
// (aparência, navegação e formatos). O modelo de dados fica em ./data-model/; Geral e Segurança da instituição em
// ./settings-workspace-pages.js.
import { html } from 'htm/preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { PageCard, PageHeader } from '../page/page-card.js';
import { COLOR_SCHEMES, colorSchemeState, setColorScheme } from '../../app/theme.js';
import { currentWorkspaceMemberState, replaceCurrentWorkspaceMemberLocally, sessionState } from '../../data/metadata-store.js';
import { releaseUploadedImage, updateOwnProfile, uploadImage } from '../../data/workspace-settings.js';
import { formatFullName } from '../../lib/field-metadata.js';
import { formatPreferencesState } from '../../lib/format-preferences.js';
import {
  CALENDAR_START_DAYS, DATE_FORMATS, NUMBER_FORMATS, OPEN_RECORD_IN_OPTIONS, TIME_FORMATS, TIME_ZONE_OPTIONS,
} from '../../../../shared/settings/format-preferences.js';
import { Icon } from '../../ui/icon.js';
import { showErrorSnackbar, showSuccessSnackbar } from '../../ui/snackbar.js';
import { ImageUploadField } from './image-upload-field.js';
import { t } from '../../i18n/index.js';

// auto-save do nome, como o debounce de 500 ms do Twenty
const NAME_SAVE_DELAY_MILLISECONDS = 500;

/**
 * @param {{ title: string, description?: string, children: any }} props
 */
export function SettingsSection({ title, description, children }) {
  return html`<section class="settings-section">
    <div class="settings-section-header">
      <h2 class="settings-section-title">${title}</h2>
      ${description ? html`<p class="settings-section-description">${description}</p>` : null}
    </div>
    ${children}
  </section>`;
}

/**
 * @param {{ scheme: 'System' | 'Light' | 'Dark' }} props
 */
function ColorSchemeCard({ scheme }) {
  const isSelected = colorSchemeState.value === scheme;
  return html`<div class="color-scheme-card-container">
    <button type="button" class="color-scheme-card" data-variant=${scheme} data-selected=${isSelected ? 'true' : undefined}
      aria-label=${t(`colorSchemes.${scheme}`)} aria-pressed=${isSelected ? 'true' : 'false'} onClick=${() => setColorScheme(scheme)}>
      ${scheme === 'System'
        ? html`<span class="color-scheme-half" data-variant="Light"><span class="color-scheme-sample">Aa</span></span>
            <span class="color-scheme-half" data-variant="Dark"><span class="color-scheme-sample">Aa</span></span>`
        : html`<span class="color-scheme-sample">Aa</span>`}
      ${isSelected ? html`<span class="color-scheme-check"><${Icon} name="IconCheck" size=${14} /></span>` : null}
    </button>
    <span class="color-scheme-label">${t(`colorSchemes.${scheme}`)}</span>
  </div>`;
}

/**
 * Membro salvo: atualiza o estado local e o nome mostrado no menu.
 * @param {Record<string, any>} workspaceMember
 */
function applySavedMember(workspaceMember) {
  replaceCurrentWorkspaceMemberLocally(workspaceMember);
  const session = sessionState.value;
  if (session?.user && workspaceMember.name) {
    sessionState.value = { ...session, user: { ...session.user, firstName: workspaceMember.name.firstName, lastName: workspaceMember.name.lastName } };
  }
}

export function SettingsProfilePage() {
  const user = sessionState.value?.user;
  const member = currentWorkspaceMemberState.value;
  const [firstName, setFirstName] = useState(member?.name?.firstName ?? user?.firstName ?? '');
  const [lastName, setLastName] = useState(member?.name?.lastName ?? user?.lastName ?? '');
  const [saveStatus, setSaveStatus] = useState(/** @type {'idle' | 'saving' | 'saved'} */ ('idle'));
  const savedNameRef = useRef({ firstName, lastName });
  const fullName = formatFullName(member?.name) || [user?.firstName, user?.lastName].filter(Boolean).join(' ');
  const isFirstNameMissing = firstName.trim() === '';

  useEffect(() => {
    document.title = `${t('settings.profile.title')} - ${t('navigation.settings')}`;
  }, []);

  useEffect(() => {
    const next = { firstName: firstName.trim(), lastName: lastName.trim() };
    const saved = savedNameRef.current;
    if (next.firstName === '' || (next.firstName === saved.firstName.trim() && next.lastName === saved.lastName.trim())) return undefined;
    const timer = setTimeout(async () => {
      setSaveStatus('saving');
      try {
        const updated = await updateOwnProfile({ name: next });
        savedNameRef.current = next;
        applySavedMember(updated);
        setSaveStatus('saved');
      } catch (error) {
        setSaveStatus('idle');
        showErrorSnackbar(error);
      }
    }, NAME_SAVE_DELAY_MILLISECONDS);
    return () => clearTimeout(timer);
  }, [firstName, lastName]);

  /** @param {File} file */
  const uploadPicture = async (file) => {
    const previous = member?.avatarUrl ?? null;
    const uploaded = await uploadImage(file);
    const updated = await updateOwnProfile({ avatarUrl: uploaded.url });
    applySavedMember(updated);
    if (previous) await releaseUploadedImage(previous);
    showSuccessSnackbar(t('workspaceSettings.profile.pictureSaved'));
  };

  const removePicture = async () => {
    const previous = member?.avatarUrl ?? null;
    try {
      applySavedMember(await updateOwnProfile({ avatarUrl: null }));
      if (previous) await releaseUploadedImage(previous);
    } catch (error) {
      showErrorSnackbar(error);
    }
  };

  return html`<${PageCard} header=${html`<${PageHeader} icon="IconUserCircle" title=${t('settings.profile.title')} />`}>
    <div class="settings-page">
      <${SettingsSection} title=${t('workspaceSettings.profile.picture')}>
        <${ImageUploadField} imageUrl=${member?.avatarUrl ?? null} placeholder=${fullName} seed=${member?.id ?? user?.id ?? fullName} shape="rounded"
          cropTitle=${t('workspaceSettings.profile.cropTitle')} onUpload=${uploadPicture} onRemove=${removePicture} />
      <//>
      <${SettingsSection} title=${t('workspaceSettings.profile.name')} description=${t('workspaceSettings.profile.nameDescription')}>
        <div class="settings-form-row">
          <label class="form-field">
            <span class="form-field-label">${t('auth.fields.firstName')}</span>
            <input class="text-input" name="firstName" value=${firstName} maxLength=${60} autocomplete="given-name" data-error=${isFirstNameMissing ? 'true' : undefined}
              onInput=${(event) => setFirstName(/** @type {HTMLInputElement} */ (event.currentTarget).value)} />
          </label>
          <label class="form-field">
            <span class="form-field-label">${t('auth.fields.lastName')}</span>
            <input class="text-input" name="lastName" value=${lastName} maxLength=${60} autocomplete="family-name"
              onInput=${(event) => setLastName(/** @type {HTMLInputElement} */ (event.currentTarget).value)} />
          </label>
        </div>
        <span class="settings-field-status" role="status" data-danger=${isFirstNameMissing ? 'true' : undefined}>
          ${isFirstNameMissing ? t('workspaceSettings.profile.firstNameRequired')
            : saveStatus === 'saving' ? t('workspaceSettings.saving') : saveStatus === 'saved' ? t('workspaceSettings.saved') : ''}
        </span>
      <//>
      <${SettingsSection} title=${t('workspaceSettings.profile.email')} description=${t('workspaceSettings.profile.emailDescription')}>
        <label class="form-field">
          <span class="form-field-label">${t('auth.fields.email')}</span>
          <input class="text-input" type="email" value=${user?.email ?? member?.userEmail ?? ''} readonly aria-readonly="true" />
        </label>
        <span class="settings-field-status">${t('workspaceSettings.profile.emailReadOnly')}</span>
      <//>
    </div>
  <//>`;
}

/**
 * Select nativo de preferência (lista curta, acessível no celular).
 * @param {{ label: string, value: string, options: Array<{ value: string, label: string }>, onChange: (value: string) => void, name: string }} props
 */
function PreferenceSelect({ label, value, options, onChange, name }) {
  return html`<label class="form-field">
    <span class="form-field-label">${label}</span>
    <select class="text-input settings-select" name=${name} value=${value}
      onChange=${(event) => onChange(/** @type {HTMLSelectElement} */ (event.currentTarget).value)}>
      ${options.map((option) => html`<option key=${option.value} value=${option.value}>${option.label}</option>`)}
    </select>
  </label>`;
}

/** "(GMT-03:00) São Paulo" para a lista de fusos. @param {string} timeZone */
function timeZoneLabel(timeZone) {
  let offset = '';
  try {
    const part = new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'longOffset' }).formatToParts(new Date()).find((item) => item.type === 'timeZoneName');
    offset = part?.value === 'GMT' ? 'GMT+00:00' : part?.value ?? '';
  } catch {
    offset = '';
  }
  const city = t(`workspaceSettings.timeZones.${timeZone.replaceAll('/', '_')}`);
  const name = city.startsWith('workspaceSettings.') ? timeZone.split('/').pop()?.replaceAll('_', ' ') ?? timeZone : city;
  return offset ? `(${offset}) ${name}` : name;
}

export function SettingsExperiencePage() {
  const preferences = formatPreferencesState.value;
  const [savingKey, setSavingKey] = useState(/** @type {string | null} */ (null));
  useEffect(() => {
    document.title = `${t('workspaceSettings.experience.title')} - ${t('navigation.settings')}`;
  }, []);

  /** @param {string} key @param {string | number} value */
  const savePreference = async (key, value) => {
    setSavingKey(key);
    try {
      applySavedMember(await updateOwnProfile({ [key]: value }));
      showSuccessSnackbar(t('workspaceSettings.experience.saved'));
    } catch (error) {
      showErrorSnackbar(error);
    } finally {
      setSavingKey(null);
    }
  };

  // exemplos com a data e a hora de agora, como os rótulos do Twenty
  const now = new Date();
  const [year, month, day] = now.toISOString().slice(0, 10).split('-');
  /** @type {Record<string, string>} */
  const dateSamples = { DAY_FIRST: `${day}/${month}/${year}`, YEAR_FIRST: `${year}-${month}-${day}` };
  /** @param {string} format */
  const timeSample = (format) => new Intl.DateTimeFormat(format === 'HOUR_12' ? 'en-US' : 'pt-BR', {
    hour: format === 'HOUR_12' ? 'numeric' : '2-digit', minute: '2-digit', hour12: format === 'HOUR_12', timeZone: preferences.timeZone,
  }).format(now);
  /** @param {string} format */
  const numberSample = (format) => new Intl.NumberFormat(format === 'COMMAS_AND_DOT' ? 'en-US' : 'pt-BR', { minimumFractionDigits: 2 }).format(1234.56);

  return html`<${PageCard} header=${html`<${PageHeader} icon="IconColorSwatch" title=${t('workspaceSettings.experience.title')} />`}>
    <div class="settings-page" aria-busy=${savingKey ? 'true' : undefined}>
      <${SettingsSection} title=${t('settings.profile.appearance')} description=${t('settings.profile.appearanceDescription')}>
        <div class="color-scheme-picker">
          ${COLOR_SCHEMES.map((scheme) => html`<${ColorSchemeCard} key=${scheme} scheme=${scheme} />`)}
        </div>
      <//>
      <${SettingsSection} title=${t('workspaceSettings.experience.navigation')} description=${t('workspaceSettings.experience.navigationDescription')}>
        <div class="settings-radio-cards" role="radiogroup" aria-label=${t('workspaceSettings.experience.openRecordIn')}>
          ${OPEN_RECORD_IN_OPTIONS.map((option) => html`<label key=${option} class="settings-radio-card" data-selected=${preferences.openRecordIn === option ? 'true' : undefined}>
            <input type="radio" name="openRecordIn" value=${option} checked=${preferences.openRecordIn === option}
              onChange=${() => savePreference('openRecordIn', option)} />
            <${Icon} name=${option === 'SIDE_PANEL' ? 'IconLayoutSidebarRight' : 'IconFileText'} size=${18} />
            <span class="settings-radio-card-texts">
              <span class="settings-radio-card-title">${t(`workspaceSettings.experience.openRecordInOptions.${option}.title`)}</span>
              <span class="settings-radio-card-description">${t(`workspaceSettings.experience.openRecordInOptions.${option}.description`)}</span>
            </span>
          </label>`)}
        </div>
      <//>
      <${SettingsSection} title=${t('workspaceSettings.experience.formats')} description=${t('workspaceSettings.experience.formatsDescription')}>
        <div class="settings-form-stack">
          <${PreferenceSelect} name="timeZone" label=${t('workspaceSettings.experience.timeZone')} value=${preferences.timeZone}
            options=${[...new Set([preferences.timeZone, ...TIME_ZONE_OPTIONS])].map((timeZone) => ({ value: timeZone, label: timeZoneLabel(timeZone) }))}
            onChange=${(value) => savePreference('timeZone', value)} />
          <${PreferenceSelect} name="dateFormat" label=${t('workspaceSettings.experience.dateFormat')} value=${preferences.dateFormat}
            options=${DATE_FORMATS.map((format) => ({
              value: format, label: `${t(`workspaceSettings.experience.dateFormats.${format}`)} (${dateSamples[format]})`,
            }))}
            onChange=${(value) => savePreference('dateFormat', value)} />
          <${PreferenceSelect} name="timeFormat" label=${t('workspaceSettings.experience.timeFormat')} value=${preferences.timeFormat}
            options=${TIME_FORMATS.map((format) => ({
              value: format, label: `${t(`workspaceSettings.experience.timeFormats.${format}`)} (${timeSample(format)})`,
            }))}
            onChange=${(value) => savePreference('timeFormat', value)} />
          <${PreferenceSelect} name="numberFormat" label=${t('workspaceSettings.experience.numberFormat')} value=${preferences.numberFormat}
            options=${NUMBER_FORMATS.map((format) => ({
              value: format, label: numberSample(format),
            }))}
            onChange=${(value) => savePreference('numberFormat', value)} />
          <${PreferenceSelect} name="calendarStartDay" label=${t('workspaceSettings.experience.calendarStartDay')} value=${String(preferences.calendarStartDay)}
            options=${CALENDAR_START_DAYS.map((day) => ({ value: String(day), label: t(`workspaceSettings.experience.weekDays.${day}`) }))}
            onChange=${(value) => savePreference('calendarStartDay', Number(value))} />
        </div>
      <//>
    </div>
  <//>`;
}
